import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Phone, Eye, Send, MessageCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { settingsApi, followupApi } from "@/lib/crmApi";

const DEFAULT_TEMPLATE =
  "Bonjour {prenom} 😊\nJe me permets de revenir vers vous. Avez-vous pu consulter le catalogue ?";

/** Mirrors backend `_format_welcome_template` (used by both flows) */
function renderTemplate(tpl, nom) {
  const fullName = (nom || "").trim();
  const prenom = fullName ? fullName.split(/\s+/)[0] : "";
  try {
    return (tpl || "")
      .replace(/\{nom\}/g, fullName)
      .replace(/\{prenom\}/g, prenom);
  } catch {
    return tpl || "";
  }
}

export default function FollowupAutoSend() {
  const [enabled, setEnabled] = useState(true);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [initial, setInitial] = useState({ enabled: true, template: DEFAULT_TEMPLATE });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [previewName, setPreviewName] = useState("Mariama Diop");
  const [testPhone, setTestPhone] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    let cancelled = false;
    settingsApi
      .get()
      .then((s) => {
        if (cancelled) return;
        const en = s.wa_followup_enabled !== false; // default true if missing
        const tpl = s.wa_followup_template || DEFAULT_TEMPLATE;
        setEnabled(en);
        setTemplate(tpl);
        setInitial({ enabled: en, template: tpl });
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty =
    enabled !== initial.enabled || template !== initial.template;

  const previewText = useMemo(
    () => renderTemplate(template, previewName),
    [template, previewName]
  );

  const save = async () => {
    if (enabled && !template.trim()) {
      toast.error("Le message ne peut pas être vide");
      return;
    }
    setSaving(true);
    try {
      const s = await settingsApi.update({
        wa_followup_enabled: enabled,
        wa_followup_template: template,
      });
      setInitial({
        enabled: s.wa_followup_enabled !== false,
        template: s.wa_followup_template || DEFAULT_TEMPLATE,
      });
      toast.success("Relance enregistrée");
    } catch (e) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone.trim()) return toast.error("Indique un numéro de téléphone");
    if (!template.trim()) return toast.error("Le message est vide");
    if (dirty) {
      toast.error("Enregistre d'abord tes modifications avant de tester");
      return;
    }
    setSendingTest(true);
    try {
      await followupApi.testSend({ phone: testPhone.trim(), name: previewName });
      toast.success("Relance de test envoyée sur WhatsApp");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.humanMessage ||
        err?.message ||
        "Échec de l'envoi";
      toast.error(typeof msg === "string" ? msg : "Échec de l'envoi");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-border/60 bg-card p-5 shadow-soft sm:p-6"
      data-testid="followup-autosend"
    >
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300">
          <Phone className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-base font-bold">
            Relance automatique 4h sans réponse
          </h3>
          <p className="text-xs text-muted-foreground">
            Envoyée si le prospect n'a pas répondu 4h après le message de bienvenue.
            Annulée automatiquement dès qu'il répond.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={setEnabled}
          disabled={loading}
          data-testid="followup-toggle"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="followup-template">Texte du message</Label>
        <Textarea
          id="followup-template"
          value={template}
          onChange={(e) => setTemplate(e.target.value)}
          rows={4}
          placeholder={DEFAULT_TEMPLATE}
          disabled={loading || !enabled}
          data-testid="followup-template"
        />
        <p className="text-xs text-muted-foreground">
          Variables : <code className="rounded bg-muted px-1">{"{nom}"}</code>,{" "}
          <code className="rounded bg-muted px-1">{"{prenom}"}</code>
        </p>
      </div>

      {/* Live preview */}
      <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50/50 p-3 dark:border-orange-900/40 dark:bg-orange-950/20">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Eye className="h-4 w-4 text-orange-700 dark:text-orange-300" />
          <span className="text-xs font-bold uppercase tracking-wide text-orange-700 dark:text-orange-300">
            Aperçu pour
          </span>
          <Input
            value={previewName}
            onChange={(e) => setPreviewName(e.target.value)}
            placeholder="Nom du prospect"
            className="h-7 max-w-[180px] border-orange-300 bg-white text-xs dark:border-orange-800 dark:bg-zinc-900"
            data-testid="followup-preview-name"
          />
        </div>
        <div className="flex items-start gap-2">
          <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-orange-600 text-white">
            <MessageCircle className="h-3.5 w-3.5" />
          </div>
          <div
            className="min-h-[40px] flex-1 whitespace-pre-wrap rounded-2xl rounded-tl-none bg-white px-3 py-2 text-sm leading-relaxed text-zinc-900 shadow-sm dark:bg-zinc-900 dark:text-zinc-100"
            data-testid="followup-preview-rendered"
          >
            {previewText || (
              <span className="italic text-muted-foreground">
                (le message apparaîtra ici)
              </span>
            )}
          </div>
        </div>
        <p className="mt-2 text-[10px] text-muted-foreground">
          Aperçu en temps réel — c'est exactement ce qui partira si le client n'a pas répondu sous 4h.
        </p>
      </div>

      {/* Save + Send test */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-1 flex-col gap-1.5 sm:max-w-md">
          <Label htmlFor="followup-test-phone" className="text-[11px]">
            Tester sur un numéro (envoi WhatsApp réel)
          </Label>
          <div className="flex gap-2">
            <Input
              id="followup-test-phone"
              type="tel"
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+221 77 555 11 22"
              className="flex-1"
              data-testid="followup-test-phone"
            />
            <Button
              variant="outline"
              onClick={sendTest}
              disabled={sendingTest || loading || !testPhone.trim()}
              className="gap-1.5"
              data-testid="followup-test-send"
            >
              {sendingTest ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sendingTest ? "Envoi…" : "Envoyer test"}
            </Button>
          </div>
        </div>

        <Button
          onClick={save}
          disabled={!dirty || saving || loading}
          className="gap-2"
          data-testid="followup-save"
        >
          <Save className="h-4 w-4" />
          {saving ? "Enregistrement…" : "Enregistrer"}
        </Button>
      </div>

      {!enabled && (
        <p className="mt-3 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          ⚠️ Désactivée : aucune relance automatique n'est programmée pour les
          nouveaux leads. Les relances déjà en queue continueront à se déclencher
          (annule-les manuellement dans « Envois programmés » si besoin).
        </p>
      )}
    </section>
  );
}
